import React, {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Link,
  useHistory,
  useLocation,
} from 'react-router-dom/cjs/react-router-dom.min';
import { logOut, openSideNav, registerRoutes } from '../../func';
import Axios from 'axios';
import M from '@materializecss/materialize';
import moment from 'moment';

const OutboxSearch = () => {
  const location = useLocation();
  const history = useHistory();
  const { pathname } = location;
  const ref = useRef({
    abortController: new AbortController(),
    employeeAbortController: new AbortController(),
  });

  const initFilters = () => ({
    senderEmailAddress: 'reports@outdaterx.com',
    sentById: '',
    recipientEmailAddress: '',
    dateStart: '',
    dateEnd: '',
    subject: '',
  });

  const [loading, setLoading] = useState(false);
  const [emails, setEmails] = useState([]);
  const [filters, setFilters] = useState(initFilters());
  const [employees, setEmployees] = useState([]);

  const authData = useMemo(
    () => JSON.parse(localStorage.getItem('auth_data') || ''),
    []
  );
  const networkCalls = useMemo(
    () =>
      registerRoutes(
        {
          searchEmails: {
            func: filters => {
              setLoading(true);
              ref.current.abortController?.abort();
              ref.current.abortController = new AbortController();

              Axios.get('/api/v2/outbox/read/many/search', {
                params: {
                  ...filters,
                  ...authData,
                },
                signal: ref.current.abortController.signal,
              })
                .then(result => setEmails(result.data))
                .catch(logOut)
                .finally(() => setLoading(false));
            },
            type: 'r',
          },
          getEmployees: {
            func: () => {
              Axios.get('/api/v1/employee/read/all', {
                params: authData,
                signal: ref.current?.employeeAbortController?.abort(),
              })
                .then(result => {
                  setEmployees(result.data);
                })
                .catch(logOut);
            },
            type: 'r',
          },
        },
        pathname
      ),
    [pathname]
  );

  useEffect(() => {
    networkCalls.getEmployees();
    return () => {
      ref.current.abortController?.abort();
      ref.current.employeeAbortController?.abort();
    };
  }, []);

  useEffect(() => {
    const { search } = location;
    if (search) {
      const params = new URLSearchParams(search);
      const filters = {};
      params.forEach((value, key) => (filters[key] = value || ''));
      setFilters(filters);
      networkCalls.searchEmails(filters);
    }
  }, [location]);

  useEffect(() => {
    M.updateTextFields();
  }, [filters]);

  useLayoutEffect(() => {
    M.FormSelect.init(document.querySelectorAll('select'));
  }, [employees]);

  const onChange = ({ target: { id, value } }) =>
    setFilters(prev => ({ ...prev, [id]: value }));

  const search = event => {
    event?.preventDefault();
    const params = new URLSearchParams(filters);
    history.push(`/outbox?${params.toString()}`);
    networkCalls.searchEmails(filters);
  };

  const goToEmail = id => {
    history.push(`/outbox/${id}`);
  };

  return (
    <div className="main">
      <div className="row">
        <div style={{ display: 'flex' }}>
          <Link to="/" onClick={openSideNav} style={{ marginRight: '12px' }}>
            <i className="material-icons black-text">menu</i>
          </Link>
          <Link to="/">Home</Link>
          <i className="material-icons">chevron_right</i>
          <span className="grey-text">Search Outbox</span>
        </div>
      </div>
      <div className="row">
        <div className="input-field col s12 m4">
          <input
            id="senderEmailAddress"
            type="text"
            value={filters.senderEmailAddress}
            onChange={onChange}
          />
          <label htmlFor="senderEmailAddress">Sender</label>
        </div>
        <div className="input-field col s12 m4">
          <input
            id="recipientEmailAddress"
            type="text"
            value={filters.recipientEmailAddress}
            onChange={onChange}
          />
          <label htmlFor="recipientEmailAddress">Recipient</label>
        </div>
        <div className="input-field col s12 m4">
          <select id="sentById" value={filters.sentById} onChange={onChange}>
            <option value={''}></option>
            {employees?.map(({ id, firstName, lastName }) => (
              <option key={id} value={id}>
                {firstName} {lastName}
              </option>
            ))}
          </select>
          <label htmlFor="sentById">Sent By</label>
        </div>
      </div>
      <div className="row">
        <div className="input-field col s12 m6">
          <input
            id="subject"
            type="text"
            value={filters.subject}
            onChange={onChange}
          />
          <label htmlFor="subject">Subject</label>
        </div>
        <div className="input-field col s12 m3">
          <input
            id="dateStart"
            type="date"
            value={filters.dateStart}
            onChange={onChange}
          />
          <label htmlFor="dateStart">Date Start</label>
        </div>
        <div className="input-field col s12 m3">
          <input
            id="dateEnd"
            type="date"
            value={filters.dateEnd}
            onChange={onChange}
          />
          <label htmlFor="dateEnd">Date End</label>
        </div>
      </div>
      <div className="row">
        <div className="input-field col s12 m3 offset-m9">
          <a
            href="/"
            className="btn-small blue white-text wave-effect waves-light col s12"
            onClick={search}
            disabled={loading}
          >
            Search
          </a>
        </div>
      </div>
      <div className="row">
        {loading ? (
          <div className="progress blue">
            <div className="indeterminate" />
          </div>
        ) : (
          <table className="highlight">
            <thead>
              <tr>
                <th>Subject</th>
                <th>Sender</th>
                <th>Recipients</th>
                <th>Sent By</th>
                <th>Timestamp</th>
              </tr>
            </thead>
            <tbody>
              {emails?.map(
                ({
                  subject,
                  id,
                  senderEmailAddress,
                  sentBy,
                  timestamp,
                  recipients,
                }) => (
                  <tr
                    key={id}
                    style={{ cursor: 'pointer' }}
                    onClick={() => goToEmail(id)}
                  >
                    <td style={{ padding: '2px' }}>{subject}</td>
                    <td style={{ padding: '2px' }}>{senderEmailAddress}</td>
                    <td style={{ padding: '2px' }}>
                      {recipients
                        .map(({ emailAddress }) => emailAddress)
                        .join(', ')}
                    </td>
                    <td style={{ padding: '2px' }}>
                      {sentBy.firstName} {sentBy.lastName}
                    </td>
                    <td style={{ padding: '2px' }}>
                      {moment(timestamp).format('MM/DD/YY HH:mm:ss')}
                    </td>
                  </tr>
                )
              )}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default OutboxSearch;
